import React, { useEffect, useMemo, useState } from 'react';
import './blocked-event.scss';
import reportService from '../../../../services/report.service';
import { useStores } from '../../../store';
import { BlockedEvent } from '@src/dtos/reports-data-response';
import Loader from '../../../shared/loader/loader';
import { Direction, PolicyMode } from '@src/utils/enums';
import moment from 'moment';
import { SHORT_MONTH_DAY_YEAR_FORMAT } from '@src/utils/datesUtils';
import { colors } from '@src/utils/colors';
import DirectionSelect from 'src/components/shared/DirectionSelect/DirectionSelect';
import { observer } from 'mobx-react-lite';
import { TimeRangeProps } from '@src/dtos/reports-data-request';
import { TryAgainMessage } from 'src/components/shared/try-again-message/try-again-message';
import { Link } from 'react-router-dom';

const policyTypeColors: { [key: string]: string } = {
    'Countries': colors.primaryBlue,
    'Apps': colors.yellow,
    'Services': colors.orange,
    'Dynamic Defense Shield': colors.secondaryBlue,
    'Web Filtering': colors.lightBlue,
    'Premium Threat Protection': colors.purple,
    'DNS Protection': colors.purple,
};
export const BlockedEvents: React.FC<TimeRangeProps> = observer(({ from, to }) => {
    const [blockedEvents, setBlockedEvents] = useState<BlockedEvent[]>([]);
    const [hasError, setHasError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [direction, setDirection] = useState<Direction>(Direction.Inbound);
    const { customerStore, fwVersionStore } = useStores();

    useEffect(() => {
        setHasError(false);
        setIsLoading(true);
        reportService.getBlockedEvent(customerStore.customerId, customerStore.serviceId, direction.toLowerCase(), from, to)
            .then(res => setBlockedEvents(res.blockedEvents))
            .catch(() => setHasError(true))
            .finally(() => setIsLoading(false));
    }, [from, to, direction, customerStore.customerId, customerStore.serviceId]);

    const max = useMemo(() => blockedEvents[0]?.count, [blockedEvents]);

    const path = useMemo(() => `/${customerStore.customerId}/${customerStore.serviceId}/insights/reports/`,
        [customerStore.customerId, customerStore.serviceId, fwVersionStore.selectedVersionId]);

    const isSmart = useMemo(() =>
        customerStore.policyMode === PolicyMode.SMART_POLICY && fwVersionStore.isOneVersionActive,
        [customerStore.policyMode, fwVersionStore.isOneVersionActive]);

    const isLinkDisabled = (reportType: string | undefined, policySectionType: string | undefined) => {
        return isSmart || !reportType || direction === Direction.Inbound && policySectionType === 'Dynamic Defense Shield';
    };

    const renderContent = useMemo(() => {
        if (hasError) return <TryAgainMessage />;
        return <div className="block-events-list">
            <div className="block-events-list-header">
                <div className="cell">TYPE</div>
                <div className="cell">SESSIONS</div>
            </div>
            <div className="block-events-list-content">
                {blockedEvents.map(({ policySectionType, count, reportType }) => (
                    <div className={`policy-item ${policySectionType.replaceAll(/\s/g, '-').toLowerCase()}`} key={policySectionType}>
                        <Link
                            className={`type-cell ${isLinkDisabled(reportType, policySectionType) ? 'disable' : ''}`}
                            to={`${path}${reportType}`}
                            state={{ direction }}>
                            {policySectionType}
                        </Link>
                        <div className={`count-cell ${isSmart && !count ? 'empty' : ''}`}>
                            {count || isSmart ? <>
                                <div className="bar-chart-wrapper"
                                    style={{ width: `${isSmart && !count ? 100 : (count / max) * 100}%` }}>
                                    <div className="bar-chart"
                                        style={{
                                            width: '100%',
                                            background: policyTypeColors[policySectionType]
                                        }} />
                                </div>
                                <div className="count-val">{count.toLocaleString()}</div>
                            </> : <div>No blocks</div>}
                        </div>
                    </div>
                ))}
            </div>
        </div>;
    }, [hasError, blockedEvents, isSmart]);

    return (
        <div className="blocked-events">
            <div className="blocked-events-header">
                <div className="blocked-events-title">Blocked Sessions</div>
                <DirectionSelect value={direction} onChange={value => setDirection(value as Direction)} />
                <div className="blocked-event-time-range">
                    {moment(from).format(SHORT_MONTH_DAY_YEAR_FORMAT)} - {moment(to).format(SHORT_MONTH_DAY_YEAR_FORMAT)}
                </div>
            </div>
            {isLoading ? <Loader type="component" /> : renderContent}
        </div>
    );
});